import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import SuperuserPrimeReportsPanel from './SuperuserPrimeReportsPanel';

interface WeeklySeriesPoint {
  week_start: string;
  week_end: string;
  tokens_used?: number;
  active_users?: number;
}

interface DailyActivePoint {
  date: string;
  active_users: number;
}

interface WeeklyReportOrder {
  id: number;
  created_at: string | null;
  transaction_type: string;
  token_cost: number;
  user_id: number;
  user_email: string;
  org_name: string | null;
  report_name: string;
}

interface UserActivitySummaryRow {
  user_id: number;
  user_email: string;
  org_name: string | null;
  login_count: number;
  report_orders_count: number;
}

interface DashboardStatsResponse {
  current_week_tokens: number;
  current_week_users: number;
  weekly_token_usage: WeeklySeriesPoint[];
  weekly_active_users: WeeklySeriesPoint[];
  weekly_report_orders: WeeklyReportOrder[];
  weekly_reports_total: number;
  user_activity_summary: UserActivitySummaryRow[];
  orders_window_start: string;
  orders_window_end: string;
  orders_page: number;
  has_older_orders: boolean;
  all_users: {
    user_id: number;
    user_email: string;
    marketing_opt_out: boolean;
    is_active: boolean;
  }[];
  all_organizations: { org_id: number; org_name: string }[];
}

const formatDateTime = (iso: string | null) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const SuperuserDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [selectedOrg, setSelectedOrg] = useState<string>('all');
  const [page, setPage] = useState<number>(0);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiService.fetchSuperuserDashboardStats(page);
        setStats(data);
      } catch (e: any) {
        console.error('Error loading superuser dashboard stats', e);
        setError(
          e?.response?.data?.error ||
            e?.message ||
            'Failed to load dashboard statistics'
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [page]);

  const uniqueUsers =
    stats?.all_users
      .map((u) => u.user_email)
      .filter((v, i, arr) => v && arr.indexOf(v) === i)
      .sort((a, b) => a.localeCompare(b)) || [];

  const uniqueOrgs =
    stats?.all_organizations
      .map((o) => o.org_name)
      .filter((v, i, arr) => v && arr.indexOf(v) === i)
      .sort((a, b) => a.localeCompare(b)) || [];

  const filteredOrders =
    stats?.weekly_report_orders.filter((o) => {
      const userMatch =
        selectedUser === 'all' || o.user_email === selectedUser;
      const orgMatch =
        selectedOrg === 'all' ||
        (o.org_name || '') === selectedOrg;
      return userMatch && orgMatch;
    }) || [];

  return (
    <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              Superuser Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Overview of report orders and user activity.
            </p>
          </div>
        </div>

        {loading && (
          <div className="bg-white rounded-2xl shadow-card border border-slate-200 p-6">
            <p className="text-slate-500 text-sm">Loading statistics…</p>
          </div>
        )}

        {error && !loading && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-2xl p-4 text-sm">
            {error}
          </div>
        )}

        {stats && !loading && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl shadow-card border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Tokens Used (This Week)
                </p>
                <p className="mt-2 text-2xl font-bold text-slate-900">
                  {stats.current_week_tokens.toLocaleString()}
                </p>
              </div>
              <div className="bg-white rounded-2xl shadow-card border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Active Users (This Week)
                </p>
                <p className="mt-2 text-2xl font-bold text-slate-900">
                  {stats.current_week_users.toLocaleString()}
                </p>
              </div>
              <div className="bg-white rounded-2xl shadow-card border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Reports (Last 7 Days)
                </p>
                <p className="mt-2 text-2xl font-bold text-slate-900">
                  {stats.weekly_reports_total.toLocaleString()}
                </p>
              </div>
              <div className="bg-white rounded-2xl shadow-card border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Users With Sessions (Last 7 Days)
                </p>
                <p className="mt-2 text-2xl font-bold text-slate-900">
                  {stats.user_activity_summary.length.toLocaleString()}
                </p>
              </div>
            </div>

            {/* User Activity Summary - above Report Orders */}
            <div className="bg-white rounded-2xl shadow-card border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">
                  User Activity Summary (7-Day Window)
                </h2>
              </div>
              {stats.user_activity_summary.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No user activity in this period.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
                        <th className="py-2 text-left">User</th>
                        <th className="py-2 text-left">Organization</th>
                        <th className="py-2 text-right">Logins</th>
                        <th className="py-2 text-right">Reports ordered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.user_activity_summary.map((u) => (
                        <tr
                          key={u.user_id}
                          className="border-b border-slate-100 last:border-0"
                        >
                          <td className="py-1.5">{u.user_email}</td>
                          <td className="py-1.5">{u.org_name || '—'}</td>
                          <td className="py-1.5 text-right">
                            {u.login_count.toLocaleString()}
                          </td>
                          <td className="py-1.5 text-right">
                            {(u.report_orders_count ?? 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Weekly Report Orders */}
            <div className="bg-white rounded-2xl shadow-card border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">
                  Report Orders (7-Day Window)
                </h2>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  {stats.orders_window_start && stats.orders_window_end && (
                    <p className="whitespace-nowrap">
                      {stats.orders_window_start} – {stats.orders_window_end}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap">User</span>
                    <select
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                      className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white"
                    >
                      <option value="all">All</option>
                      {uniqueUsers.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap">Organization</span>
                    <select
                      value={selectedOrg}
                      onChange={(e) => setSelectedOrg(e.target.value)}
                      className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white"
                    >
                      <option value="all">All</option>
                      {uniqueOrgs.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="whitespace-nowrap">
                    Showing{' '}
                    <span className="font-semibold text-slate-700">
                      {filteredOrders.length.toLocaleString()}
                    </span>{' '}
                    of{' '}
                    <span className="font-semibold text-slate-700">
                      {stats.weekly_reports_total.toLocaleString()}
                    </span>
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page <= 0}
                      className={`px-2 py-1 rounded-lg border text-xs ${
                        page <= 0
                          ? 'border-slate-200 text-slate-300 cursor-not-allowed'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      Newer
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={!stats.has_older_orders}
                      className={`px-2 py-1 rounded-lg border text-xs ${
                        !stats.has_older_orders
                          ? 'border-slate-200 text-slate-300 cursor-not-allowed'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      Older
                    </button>
                  </div>
                </div>
              </div>
              {filteredOrders.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No report orders in the last 7 days.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
                        <th className="py-2 text-left">Requested At</th>
                        <th className="py-2 text-left">User</th>
                        <th className="py-2 text-left">Organization</th>
                        <th className="py-2 text-left">Report</th>
                        <th className="py-2 text-left">Type</th>
                        <th className="py-2 text-right">Tokens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((r) => (
                        <tr
                          key={r.id}
                          className="border-b border-slate-100 last:border-0"
                        >
                          <td className="py-1.5 whitespace-nowrap">
                            {formatDateTime(r.created_at)}
                          </td>
                          <td className="py-1.5">{r.user_email}</td>
                          <td className="py-1.5">{r.org_name || '—'}</td>
                          <td className="py-1.5">{r.report_name}</td>
                          <td className="py-1.5">{r.transaction_type}</td>
                          <td className="py-1.5 text-right">
                            {r.token_cost.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <SuperuserPrimeReportsPanel />
          </>
        )}
      </div>
  );
};

export default SuperuserDashboard;

